/**
 * Утилиты для работы с DOM-элементами
 */

/**
 * Находит элемент по набору селекторов.
 * Пытается найти по селектору последовательно, начиная с первого.
 *
 * @param {string|string[]} selectors - Селектор или массив селекторов
 * @param {HTMLElement} [rootElement=document] - Корневой элемент для поиска
 * @returns {HTMLElement|null} - Найденный элемент или null
 */
export const findElementBySelectors = (selectors, rootElement = document) => {
  if (!selectors) return null;

  // Преобразуем одиночный селектор в массив
  const selectorArray = Array.isArray(selectors) ? selectors : [selectors];

  // Проходим по всем селекторам и возвращаем первый найденный элемент
  for (const selector of selectorArray) {
    try {
      const element = rootElement.querySelector(selector);
      if (element) {
        return element;
      }
    } catch (error) {
      console.warn(`Ошибка при поиске по селектору "${selector}":`, error);
    }
  }

  return null;
};

/**
 * Находит вкладку по идентификатору, индексу или селектору
 *
 * @param {string|number} tabIdOrIndexOrSelector - ID вкладки, индекс или селектор
 * @returns {HTMLElement|null} - Найденная вкладка или null
 */
export const findTabElement = (tabIdOrIndexOrSelector) => {
  // Получаем все вкладки на странице
  const allTabs = document.querySelectorAll('[role="tab"]');

  if (allTabs.length === 0) {
    console.warn('Вкладки с атрибутом [role="tab"] не найдены на странице');
    return null;
  }

  // Если передан числовой индекс
  if (typeof tabIdOrIndexOrSelector === 'number') {
    // Проверяем, что индекс в пределах массива
    if (
      tabIdOrIndexOrSelector >= 0 &&
      tabIdOrIndexOrSelector < allTabs.length
    ) {
      return allTabs[tabIdOrIndexOrSelector];
    }
    console.warn(`Вкладка с индексом ${tabIdOrIndexOrSelector} не найдена`);
    return null;
  }

  // Если передана строка - это может быть ID вкладки или селектор
  if (typeof tabIdOrIndexOrSelector === 'string') {
    // 1. Сначала пробуем найти по data-tab-id
    const tabById = document.querySelector(
      `[role="tab"][data-tab-id="${tabIdOrIndexOrSelector}"]`
    );
    if (tabById) {
      return tabById;
    }

    // 2. Проверяем, содержит ли селектор nth-child
    const matches = tabIdOrIndexOrSelector.match(/nth-child\((\d+)\)/);
    if (matches && matches[1]) {
      const index = parseInt(matches[1]) - 1; // nth-child начинается с 1
      if (index >= 0 && index < allTabs.length) {
        return allTabs[index];
      }
    }

    // 3. Пробуем найти по селектору напрямую
    try {
      const element = document.querySelector(tabIdOrIndexOrSelector);
      if (element) {
        return element;
      }
    } catch (error) {
      console.warn(
        `Ошибка при поиске вкладки по селектору "${tabIdOrIndexOrSelector}":`,
        error
      );
    }
  }

  return null;
};

/**
 * Получает ID вкладки из DOM-элемента или индекса
 *
 * @param {HTMLElement|number} tabElementOrIndex - Элемент вкладки или её индекс
 * @returns {string|null} - ID вкладки или null, если не найден
 */
export const getTabId = (tabElementOrIndex) => {
  let tabElement = null;

  // Если передан индекс, находим элемент вкладки
  if (typeof tabElementOrIndex === 'number') {
    const allTabs = document.querySelectorAll('[role="tab"]');
    if (tabElementOrIndex >= 0 && tabElementOrIndex < allTabs.length) {
      tabElement = allTabs[tabElementOrIndex];
    } else {
      return null;
    }
  } else {
    tabElement = tabElementOrIndex;
  }

  // Проверяем, что у нас есть DOM-элемент
  if (!tabElement || !(tabElement instanceof Element)) {
    return null;
  }

  // Получаем ID из атрибута data-tab-id
  const tabId = tabElement.getAttribute('data-tab-id');
  if (tabId) {
    return tabId;
  }

  // Если data-tab-id не найден, пробуем получить индекс вкладки
  const allTabs = Array.from(document.querySelectorAll('[role="tab"]'));
  const index = allTabs.indexOf(tabElement);

  // Возвращаем стандартные ID по индексу
  if (index !== -1) {
    const standardIds = [
      'courses',
      'saved_lessons',
      'achievements',
      'badges',
      'badge_series',
      'activity',
      'tests',
      'settings',
    ];
    return index < standardIds.length ? standardIds[index] : `tab_${index}`;
  }

  return null;
};

/**
 * Преобразует индекс вкладки в ID вкладки
 *
 * @param {number} tabIndex - Индекс вкладки
 * @returns {string|null} - ID вкладки или null, если индекс недопустимый
 */
export const tabIndexToId = (tabIndex) => {
  // Проверка на корректность типа и значения
  if (typeof tabIndex !== 'number' || isNaN(tabIndex) || tabIndex < 0) {
    console.warn(`Некорректный индекс вкладки: ${tabIndex}`);
    return null;
  }

  // Безопасное обращение к массиву
  const standardIds = [
    'courses',
    'saved_lessons',
    'achievements',
    'badges',
    'badge_series',
    'activity',
    'tests',
    'settings',
  ];
  return tabIndex < standardIds.length
    ? standardIds[tabIndex]
    : `tab_${tabIndex}`;
};

/**
 * Создает обработчик событий для набора элементов
 *
 * @param {HTMLElement|HTMLElement[]} elements - Элемент или массив элементов
 * @param {string} eventType - Тип события (например, 'click')
 * @param {Function} handler - Функция-обработчик
 * @param {boolean} [useCapture=false] - Флаг использования фазы перехвата
 * @returns {Function} - Функция для удаления обработчиков
 */
export const addEventListenerToElements = (
  elements,
  eventType,
  handler,
  useCapture = false
) => {
  const elementArray = Array.isArray(elements) ? elements : [elements];

  // Добавляем обработчик каждому элементу
  elementArray.forEach((element) => {
    if (element) {
      element.addEventListener(eventType, handler, useCapture);
    }
  });

  // Возвращаем функцию для удаления обработчиков
  return () => {
    elementArray.forEach((element) => {
      if (element) {
        element.removeEventListener(eventType, handler, useCapture);
      }
    });
  };
};

/**
 * Проверяет, является ли элемент модальным окном
 * @param {HTMLElement} element - проверяемый элемент
 * @returns {boolean} - true, если элемент является модальным окном
 */
export function isModalElement(element) {
  if (!element) return false;

  // Проверяем текущий элемент
  const style = window.getComputedStyle(element);
  if (
    element.tagName === 'DIALOG' ||
    element.getAttribute('role') === 'dialog' ||
    element.classList.contains('modal') ||
    element.classList.contains('dialog') ||
    element.classList.contains('popup')
  ) {
    return true;
  }

  // Проверяем z-index - модальные окна обычно имеют высокий z-index
  if (style.position !== 'static' && parseInt(style.zIndex, 10) > 1000) {
    return true;
  }

  // Рекурсивно проверяем родителей, но не глубже 5 уровней
  let depth = 0;
  let parent = element.parentElement;
  while (parent && depth < 5) {
    if (isModalElement(parent)) return true;
    parent = parent.parentElement;
    depth++;
  }

  return false;
}

/**
 * Находит все видимые модальные окна на странице
 * @returns {Array<HTMLElement>} - массив модальных окон
 */
export function findVisibleModals() {
  const modals = [];
  const modalSelectors = [
    '.modal',
    'dialog',
    '[role="dialog"]',
    '.dialog',
    '.popup',
    '.modal-content',
  ];

  const elements = document.querySelectorAll(modalSelectors.join(','));

  elements.forEach((element) => {
    const style = window.getComputedStyle(element);
    if (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      element.offsetWidth > 0 &&
      element.offsetHeight > 0
    ) {
      modals.push(element);
    }
  });

  return modals;
}

/**
 * Определяет, полностью ли элемент виден в области просмотра
 * @param {HTMLElement} element - проверяемый элемент
 * @returns {boolean} - true, если элемент полностью виден
 */
export function isElementFullyVisible(element) {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  // Находим фиксированные элементы в верхней части страницы (шапки, навбары и т.д.)
  let headerHeight = 0;
  const possibleHeaders = document.querySelectorAll(
    'header, .header, nav, .navbar, .navigation, [role="banner"], .top-bar, .app-bar'
  );

  possibleHeaders.forEach((header) => {
    const style = window.getComputedStyle(header);
    if (
      (style.position === 'fixed' || style.position === 'sticky') &&
      parseFloat(style.top) <= 10
    ) {
      headerHeight = Math.max(headerHeight, header.offsetHeight);
    }
  });

  // Учитываем возможные перекрытия сверху
  const topVisibleThreshold = headerHeight + 10;

  return (
    rect.top >= topVisibleThreshold &&
    rect.left >= 0 &&
    rect.bottom <= windowHeight &&
    rect.right <= windowWidth
  );
}
